import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MapComponent } from './shared/components/map/map.component';
import { DashboardComponent } from './platform/web/dashboard.component';
import { VehicleManagementComponent } from './platform/web/vehicle-management.component';
import { RouteManagementComponent } from './platform/web/route-management.component';
import { TripSchedulingComponent } from './platform/web/trip-scheduling.component';
import { BookingManagementComponent } from './platform/web/booking-management.component';
import { DriverManagementComponent } from './platform/web/driver-management.component';
import { RoleGuard } from './core/guards/role.gaurd';
import { UserRole } from './core/enums/user-role.enum';
import { AuthGuard } from './core/guards/auth.gaurd';
import { PlatformOperationsOverviewComponent } from './platform/web/platform-operations-overview.component';
import { OperationsWorkspaceComponent } from './platform/web/operations-workspace.component';
import { ClientOnboardingComponent } from './platform/web/client-onboarding.component';
import { OperatorOnboardingComponent } from './platform/web/operator-onboarding.component';
import { PassengerTripSearchComponent } from './platform/web/passenger-trip-search.component';
import { PublicTicketComponent } from './platform/web/public-ticket.component';
import { AuthComponent } from './platform/web/auth.component';
import { EmailVerificationComponent } from './platform/web/email-verification.component';
import { PassengerProfilesComponent } from './platform/web/passenger-profiles.component';
import { HotspotPassengersComponent } from './platform/web/hotspot-passengers.component';
import { HotspotAssetMappingsComponent } from './platform/web/hotspot-asset-mappings.component';
import { HotspotSessionRecordsComponent } from './platform/web/hotspot-session-records.component';
import { HotspotScheduledSyncComponent } from './platform/web/hotspot-scheduled-sync.component';
import { PublicDeparturesComponent } from './platform/web/public-departures.component';
import { FeatureGuard } from './core/guards/feature.guard';
import { FeatureSettingsComponent } from './platform/web/feature-settings.component';
import { ReportingWorkspaceComponent } from './platform/web/reporting-workspace.component';

const routes: Routes = [
  { path: '', component: PublicDeparturesComponent, pathMatch: 'full' },
  { path: 'departures', component: PublicDeparturesComponent },
  { path: 'sharedtrip/:publicToken', component: PublicDeparturesComponent },
  { path: 'login', component: AuthComponent },
  { path: 'register', component: AuthComponent },
  { path: 'verify-email', component: EmailVerificationComponent },
  { path: 'email-verification/confirm', component: EmailVerificationComponent },
  { path: 'tickets/:token', component: PublicTicketComponent },
  { path: 'api/public/tickets/:token', component: PublicTicketComponent },
  { path: 'hotspot/passengers', redirectTo: 'dashboard/hotspot/passengers', pathMatch: 'full' },
  { path: 'hotspot/passengers/:id', redirectTo: 'dashboard/hotspot/passengers/:id' },
  { path: 'hotspot/asset-mappings', redirectTo: 'dashboard/hotspot/asset-mappings', pathMatch: 'full' },
  { path: 'hotspot/session-records', redirectTo: 'dashboard/hotspot/session-records', pathMatch: 'full' },
  { path: 'hotspot/scheduled-sync', redirectTo: 'dashboard/hotspot/scheduled-sync', pathMatch: 'full' },
  { path: 'passengers/profiles', redirectTo: 'dashboard/passengers/profiles', pathMatch: 'full' },
  { path: 'passengers/profiles/:id', redirectTo: 'dashboard/passengers/profiles/:id' },
  { path: 'map', component: MapComponent },
  { path: 'dashboard',
    component: DashboardComponent,
    canActivate: [AuthGuard],
    children: [
      { path: '', component: PlatformOperationsOverviewComponent, pathMatch: 'full' },
      { path: 'overview', component: PlatformOperationsOverviewComponent },
      { path: 'live-map', component: MapComponent },
      {
        path: 'utilities',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.PLATFORM_EMPLOYEE] },
        loadChildren: () => import('./features/utilities/utilities.routes').then(m => m.UTILITIES_ROUTES)
      },

      // Platform employee routes
      {
        path: 'organisations',
        component: ClientOnboardingComponent,
        //canActivate: [RoleGuard],
        data: {
          roles: [UserRole.PLATFORM_EMPLOYEE, UserRole.ADMIN],
          title: 'Operators',
          description: 'Onboard and manage operator organisations, contact details, Powerlynx location IDs, and activation status.',
          apiTag: 'Organisation'
        }
      },
      {
        path: 'users',
        component: OperationsWorkspaceComponent,
        //canActivate: [RoleGuard],
        data: {
          roles: [UserRole.PLATFORM_EMPLOYEE],
          title: 'User Administration',
          description: 'Manage platform employees, operator users, and passenger support records.',
          apiTag: 'User'
        }
      },
      {
        path: 'devices',
        component: OperationsWorkspaceComponent,
        //canActivate: [RoleGuard],
        data: {
          roles: [UserRole.PLATFORM_EMPLOYEE],
          title: 'Device Inventory',
          description: 'Manage tracking devices, assignment readiness, and installation state.',
          apiTag: 'Device'
        }
      },
      {
        path: 'device-sims',
        component: OperationsWorkspaceComponent,
        //canActivate: [RoleGuard],
        data: {
          roles: [UserRole.PLATFORM_EMPLOYEE],
          title: 'SIM Inventory',
          description: 'Track SIM allocation, network readiness, and device connectivity support.',
          apiTag: 'DeviceSIM'
        }
      },
      {
        path: 'onboarding',
        component: OperatorOnboardingComponent,
        canActivate: [RoleGuard],
        data: {
          roles: [UserRole.PLATFORM_EMPLOYEE, UserRole.ADMIN],
          title: 'Operator Onboarding',
          description: 'Complete public operator setup, link devices, generate RouterOS scripts, and validate fleet monitor readiness.',
          apiTag: 'Onboarding'
        }
      },
      {
        path: 'operations',
        redirectTo: 'reports',
        pathMatch: 'full'
      },
      
      // Operator routes
      { 
        path: 'vehicles', 
        component: VehicleManagementComponent,
        //canActivate: [RoleGuard],
        data: { roles: [UserRole.OPERATOR, UserRole.PLATFORM_EMPLOYEE] }
      },
      { 
        path: 'routes', 
        component: RouteManagementComponent,
        //canActivate: [RoleGuard],
        data: { roles: [UserRole.OPERATOR, UserRole.PLATFORM_EMPLOYEE, UserRole.ADMIN] }
      },
      { 
        path: 'trips', 
        component: TripSchedulingComponent,
        //canActivate: [RoleGuard],
        data: { roles: [UserRole.OPERATOR, UserRole.PLATFORM_EMPLOYEE] }
      },
      { 
        path: 'bookings', 
        component: BookingManagementComponent,
        canActivate: [RoleGuard, FeatureGuard],
        data: {
          roles: [UserRole.OPERATOR, UserRole.PLATFORM_EMPLOYEE, UserRole.ADMIN, UserRole.PASSENGER],
          featureKey: 'operator-booking-management'
        }
      },
      {
        path: 'features',
        component: FeatureSettingsComponent,
        canActivate: [RoleGuard],
        data: { roles: [UserRole.ADMIN] }
      },
      {
        path: 'drivers',
        component: DriverManagementComponent,
        data: {
          roles: [UserRole.OPERATOR, UserRole.PLATFORM_EMPLOYEE, UserRole.ADMIN],
          title: 'Driver Operations',
          description: 'Manage assigned drivers, contact readiness, trip allocation, and operational support.',
          apiTag: 'Drivers'
        }
      },
      {
        path: 'reports',
        component: ReportingWorkspaceComponent,
        data: {
          title: 'Reports',
          description: 'Explore catalogue-driven reports, exports and scheduled deliveries.',
          apiTag: 'Reports'
        }
      },
      {
        path: 'system-logs',
        component: OperationsWorkspaceComponent,
        canActivate: [RoleGuard],
        data: {
          roles: [UserRole.PLATFORM_EMPLOYEE, UserRole.ADMIN],
          title: 'System Logs',
          description: 'Audit queued email and message delivery outcomes without mixing operational logs into reporting.',
          apiTag: 'SystemLogs'
        }
      },
      {
        path: 'passengers/profiles',
        component: PassengerProfilesComponent,
        canActivate: [RoleGuard, FeatureGuard],
        data: {
          featureKey: 'passenger-profiles',
          roles: [UserRole.OPERATOR, UserRole.PLATFORM_EMPLOYEE, UserRole.ADMIN],
          title: 'Passenger Profiles',
          description: 'Review normalized passenger identities from app, hotspot, and imported data.',
          apiTag: 'PassengerProfile'
        }
      },
      {
        path: 'passengers/profiles/:id',
        component: PassengerProfilesComponent,
        canActivate: [RoleGuard, FeatureGuard],
        data: {
          featureKey: 'passenger-profiles',
          roles: [UserRole.OPERATOR, UserRole.PLATFORM_EMPLOYEE, UserRole.ADMIN],
          title: 'Passenger Profile Detail',
          description: 'Inspect identity, contact, linked app user, and external provider records.',
          apiTag: 'PassengerProfile'
        }
      },
      {
        path: 'hotspot/passengers',
        component: HotspotPassengersComponent,
        data: {
          roles: [UserRole.PLATFORM_EMPLOYEE, UserRole.ADMIN],
          title: 'Hotspot Sync',
          description: 'Run ad hoc Powerlynx passenger, session, and local maintenance sync actions.',
          apiTag: 'HotspotPassenger'
        }
      },
      {
        path: 'hotspot/passengers/:id',
        component: HotspotPassengersComponent,
        data: {
          roles: [UserRole.PLATFORM_EMPLOYEE, UserRole.ADMIN],
          title: 'Hotspot Passenger Detail',
          description: 'Inspect a hotspot passenger record and sync it into the passenger profile registry.',
          apiTag: 'HotspotPassenger'
        }
      },
      {
        path: 'hotspot/asset-mappings',
        component: HotspotAssetMappingsComponent,
        data: {
          roles: [UserRole.PLATFORM_EMPLOYEE, UserRole.ADMIN],
          title: 'Hotspot Asset Mappings',
          description: 'Review and correct Powerlynx location and hotspot-to-bus mappings.',
          apiTag: 'HotspotAssetMapping'
        }
      },
      {
        path: 'hotspot/session-records',
        component: HotspotSessionRecordsComponent,
        data: {
          roles: [UserRole.OPERATOR, UserRole.PLATFORM_EMPLOYEE, UserRole.ADMIN],
          title: 'Hotspot Session Records',
          description: 'Review synced hotspot session records, passenger links, and bus mapping context.',
          apiTag: 'HotspotSessionRecord'
        }
      },
      {
        path: 'hotspot/scheduled-sync',
        component: HotspotScheduledSyncComponent,
        data: {
          roles: [UserRole.PLATFORM_EMPLOYEE, UserRole.ADMIN],
          title: 'Scheduled Hotspot Sync',
          description: 'Monitor configuration-backed scheduled hotspot passenger and session sync runs.',
          apiTag: 'HotspotSyncRun'
        }
      },
      {
        path: 'search',
        component: PassengerTripSearchComponent,
        data: {
          roles: [UserRole.PASSENGER],
          title: 'Find Trips',
          description: 'Search public operators, routes, stops, and available trips.',
          apiTag: 'Public'
        }
      },
      {
        path: 'profile',
        component: OperationsWorkspaceComponent,
        data: {
          roles: [UserRole.OPERATOR, UserRole.PLATFORM_EMPLOYEE, UserRole.PASSENGER],
          title: 'Profile',
          description: 'Manage account and organisation-facing profile details.',
          apiTag: 'Profile'
        }
      }
    ]
  },
  { path: '**', redirectTo: '' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
